<template>
  <v-card class="pa-6">
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>
    <v-card-title class="text-uppercase px-0">
      {{ profile === null ? 'Crear' : 'Editar' }} perfil de redirección
    </v-card-title>
    <v-divider class="mb-4"></v-divider>
    <v-card-text class="pa-0">
      <v-form ref="profileForm" v-model="formValid">
        <v-row dense class="align-center">
          <v-col cols="12" md="8">
            <v-autocomplete
              v-model="form.company"
              :items="companyOptions"
              :search-input.sync="companySearch"
              :loading="loadingCompanies"
              :rules="[companyRule]"
              item-text="name"
              item-value="id"
              label="Empresa *"
              placeholder="Escribe para buscar"
              no-data-text="No se encontraron empresas"
              return-object
              no-filter
              outlined
              dense
              hide-details="auto"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <div v-if="form.company" class="d-flex align-center">
              <v-sheet
                v-if="form.company.logoUrl"
                outlined
                rounded
                width="120"
                height="40"
                :style="[previewBoxStyle, clickableStyle]"
                title="Ver en grande"
                class="d-flex align-center justify-center pa-1"
                @click="openViewer(form.company.logoUrl, 'Logo de ' + form.company.name)"
              >
                <img :src="form.company.logoUrl" alt="Logo de la empresa" :style="previewImgStyle" />
              </v-sheet>
              <span v-else class="text-caption grey--text text--darken-1">
                La empresa no tiene logo; la app mostrará su nombre
              </span>
            </div>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12">
            <v-text-field
              v-model="form.title"
              label="Título de la pantalla *"
              :rules="[requiredRule, maxLengthRule(255)]"
              hint="Ej.: Para este servicio, comunícate directamente con MAPFRE"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="text-subtitle-2 font-weight-bold mt-6 mb-3">
          Canales de contacto ({{ form.channels.length }}/{{ maxChannels }})
        </div>
        <v-card v-for="(channel, index) in form.channels" :key="channel.key" outlined class="pa-3 mb-3">
          <v-row dense class="align-start">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="channel.label"
                label="Etiqueta *"
                placeholder="Ej.: Lima"
                :rules="[requiredRule]"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="channel.type"
                :items="channelTypes"
                label="Tipo *"
                outlined
                dense
                hide-details="auto"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="channel.value"
                label="Valor *"
                :rules="[requiredRule, valueRule(channel.type)]"
                :hint="valueHint(channel.type)"
                persistent-hint
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense class="align-center mt-1">
            <v-col cols="12" md="9">
              <v-file-input
                v-model="channel.iconFile"
                label="Ícono (PNG, JPG o SVG, máx. 1 MB)"
                accept="image/png,image/jpeg,.svg,image/svg+xml"
                :rules="iconRules"
                :placeholder="iconFileName(channel.iconUrl)"
                :persistent-placeholder="!!channel.iconUrl"
                prepend-icon=""
                prepend-inner-icon="mdi-image-outline"
                outlined
                dense
                hide-details="auto"
                @change="onIconFileChange(channel)"
              ></v-file-input>
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center justify-end">
              <v-sheet
                outlined
                rounded
                width="40"
                height="40"
                :style="[previewBoxStyle, iconPreviewSrc(channel) && clickableStyle]"
                :title="iconPreviewSrc(channel) ? 'Ver en grande' : ''"
                class="d-flex align-center justify-center mr-2"
                @click="openViewer(iconPreviewSrc(channel), 'Ícono ' + (channel.label || ''))"
              >
                <img
                  v-if="iconPreviewSrc(channel)"
                  :src="iconPreviewSrc(channel)"
                  :alt="channel.label"
                  :style="previewImgStyle"
                />
                <v-icon v-else color="grey">{{ defaultIcon(channel.type) }}</v-icon>
              </v-sheet>
              <v-btn
                icon
                small
                title="Quitar ícono"
                :disabled="!channel.iconUrl && !channel.iconFile"
                @click="removeIcon(channel)"
              >
                <v-icon small>mdi-image-remove</v-icon>
              </v-btn>
              <v-btn icon small title="Subir" :disabled="index === 0" @click="moveChannel(index, -1)">
                <v-icon small>mdi-arrow-up</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                title="Bajar"
                :disabled="index === form.channels.length - 1"
                @click="moveChannel(index, 1)"
              >
                <v-icon small>mdi-arrow-down</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                title="Eliminar canal"
                :disabled="form.channels.length <= minChannels"
                @click="removeChannel(index)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
        <v-btn text small color="primary" :disabled="form.channels.length >= maxChannels" @click="addChannel">
          <v-icon left small>mdi-plus</v-icon>Agregar canal
        </v-btn>
      </v-form>
    </v-card-text>
    <v-alert v-if="formError" type="error" dense outlined class="mt-4 mb-2">{{ formError }}</v-alert>
    <v-divider class="mt-4"></v-divider>
    <v-card-actions class="justify-end px-0 pb-0">
      <v-btn text color="primary" rounded :disabled="loading" @click="closeDialog">Cerrar</v-btn>
      <v-btn color="primary" rounded :loading="loading" @click="validate">Guardar</v-btn>
    </v-card-actions>
    <image-preview-dialog v-model="viewer.open" :src="viewer.src" :title="viewer.title" />
  </v-card>
</template>

<script>
import RefundRedirectProfilesService from '../services/refund-redirect-profiles.service';
import CompaniesService from '../services/companie.service';
import FileUtils, { MAX_IMAGE_BYTES } from '../utils/file.utils';
import { PREVIEW_BOX_STYLE, PREVIEW_IMG_STYLE } from '../utils/image-preview.styles';
import ImagePreviewDialog from './ImagePreviewDialog.vue';

const MIN_CHANNELS = 1;
const MAX_CHANNELS = 10;
const COMPANY_PAGE_SIZE = 20;
const COMPANY_SEARCH_DELAY_MS = 350;

// Mismas reglas que el schema Zod del backend (research R5); el backend es quien decide.
const CALL_PATTERN = /^[+\d][\d\s\-()]{5,19}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CHANNEL_TYPES = [
  { text: 'Llamada', value: 'call' },
  { text: 'WhatsApp', value: 'whatsapp' },
  { text: 'Email', value: 'email' },
  { text: 'Link', value: 'link' }
];

const VALUE_HINTS = {
  call: 'Número como se marca, ej. 01-213-3333',
  whatsapp: 'Incluye código de país, ej. 51999919133',
  email: 'Ej. contacto@empresa.com',
  link: 'URL https, ej. https://www.empresa.com'
};

const DEFAULT_ICONS = {
  call: 'mdi-phone',
  whatsapp: 'mdi-whatsapp',
  email: 'mdi-email',
  link: 'mdi-link'
};

const SAVE_ERRORS = {
  REFUND_REDIRECT_COMPANY_NOT_FOUND: 'La empresa seleccionada no existe',
  INVALID_IMAGE_FORMAT: 'El ícono debe ser PNG, JPG o SVG',
  IMAGE_TOO_LARGE: 'El ícono supera 1 MB',
  UNSAFE_SVG: 'El SVG contiene contenido no permitido'
};

let channelKey = 0;

function isHttpsUrl(value) {
  try {
    return new URL(value).protocol === 'https:';
  } catch (error) {
    return false;
  }
}

// iconFile / previewUrl son estado local del formulario: nunca viajan tal cual al backend.
function buildChannel(channel = {}) {
  return {
    key: ++channelKey,
    label: channel.label || '',
    type: channel.type || 'call',
    value: channel.value || '',
    iconUrl: channel.iconUrl || null,
    iconFile: null,
    previewUrl: null
  };
}

// Todas las claves declaradas desde el inicio (reactividad de Vue 2).
function emptyProfile() {
  return { company: null, title: '', channels: [buildChannel()] };
}

export default {
  name: 'RefundRedirectProfileMaintainer',
  components: { ImagePreviewDialog },
  props: {
    profile: { type: Object, default: null }
  },
  data() {
    return {
      previewBoxStyle: PREVIEW_BOX_STYLE,
      previewImgStyle: PREVIEW_IMG_STYLE,
      clickableStyle: { cursor: 'zoom-in' },
      viewer: { open: false, src: null, title: '' },
      loading: false,
      formValid: false,
      formError: '',
      form: emptyProfile(),
      companies: [],
      companySearch: null,
      loadingCompanies: false,
      searchTimer: null,
      channelTypes: CHANNEL_TYPES,
      minChannels: MIN_CHANNELS,
      maxChannels: MAX_CHANNELS,
      iconRules: [
        (file) => !file || FileUtils.isAllowedImage(file) || 'El ícono debe ser PNG, JPG o SVG',
        (file) => !file || file.size <= MAX_IMAGE_BYTES || 'El ícono supera 1 MB'
      ]
    };
  },
  computed: {
    // La empresa elegida se mantiene entre las opciones aunque la búsqueda actual no la traiga.
    companyOptions() {
      const selected = this.form.company;
      if (!selected) return this.companies;
      return [selected, ...this.companies.filter((company) => company.id !== selected.id)];
    }
  },
  mounted() {
    this.resetForm();
    this.fetchCompanies('');
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.form.channels.forEach(this.revokePreview);
  },
  watch: {
    profile() {
      this.resetForm();
    },
    companySearch(search) {
      // Al elegir una empresa el autocomplete copia su nombre en el texto de búsqueda.
      if (this.form.company && search === this.form.company.name) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchCompanies(search || ''), COMPANY_SEARCH_DELAY_MS);
    }
  },
  methods: {
    openViewer(src, title) {
      if (!src) return;
      this.viewer = { open: true, src, title };
    },
    resetForm() {
      this.form.channels.forEach(this.revokePreview);
      this.form = this.profile === null ? emptyProfile() : this.parseProfile(this.profile);
      this.formError = '';
      this.$refs.profileForm?.resetValidation();
    },
    parseProfile(profile) {
      return {
        company: { id: profile.companyId, name: profile.counterpartName, logoUrl: profile.logoUrl || null },
        title: profile.title || '',
        channels: (profile.channels || []).map(buildChannel)
      };
    },
    async fetchCompanies(search) {
      this.loadingCompanies = true;
      const res = await CompaniesService.getCompanies(1, COMPANY_PAGE_SIZE, encodeURIComponent(search), 'all');
      this.loadingCompanies = false;
      if (res.status === 'ok') this.companies = res.data?.companies || [];
    },
    companyRule(company) {
      return !!company || 'Selecciona una empresa';
    },
    requiredRule(value) {
      return !!(value && String(value).trim()) || 'Este campo es requerido';
    },
    maxLengthRule(max) {
      return (value) => !value || value.trim().length <= max || `Máximo ${max} caracteres`;
    },
    valueRule(type) {
      return (value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return true;
        if (type === 'call') return CALL_PATTERN.test(trimmed) || 'Debe ser un número de teléfono';
        if (type === 'whatsapp') {
          const digits = trimmed.replace(/[+\s-]/g, '');
          return /^\d{8,15}$/.test(digits) || 'Debe tener entre 8 y 15 dígitos, con código de país';
        }
        if (type === 'email') return EMAIL_PATTERN.test(trimmed) || 'Debe ser un correo electrónico válido';
        if (type === 'link') return isHttpsUrl(trimmed) || 'Debe ser una URL https';
        return true;
      };
    },
    valueHint(type) {
      return VALUE_HINTS[type] || '';
    },
    defaultIcon(type) {
      return DEFAULT_ICONS[type] || 'mdi-link';
    },
    iconFileName(iconUrl) {
      return FileUtils.fileNameFromUrl(iconUrl);
    },
    iconPreviewSrc(channel) {
      return channel.previewUrl || channel.iconUrl;
    },
    revokePreview(channel) {
      if (!channel.previewUrl) return;
      URL.revokeObjectURL(channel.previewUrl);
      channel.previewUrl = null;
    },
    onIconFileChange(channel) {
      this.revokePreview(channel);
      const file = channel.iconFile;
      if (file && FileUtils.isAllowedImage(file)) channel.previewUrl = URL.createObjectURL(file);
    },
    removeIcon(channel) {
      this.revokePreview(channel);
      channel.iconFile = null;
      channel.iconUrl = null;
    },
    addChannel() {
      if (this.form.channels.length >= MAX_CHANNELS) return;
      this.form.channels.push(buildChannel());
    },
    removeChannel(index) {
      if (this.form.channels.length <= MIN_CHANNELS) return;
      const [channel] = this.form.channels.splice(index, 1);
      this.revokePreview(channel);
    },
    moveChannel(index, offset) {
      const target = index + offset;
      if (target < 0 || target >= this.form.channels.length) return;
      const [channel] = this.form.channels.splice(index, 1);
      this.form.channels.splice(target, 0, channel);
    },
    // El servicio convierte iconFile a iconBase64 (mismo patrón que plans.service.js).
    buildPayload() {
      return {
        companyId: this.form.company?.id ?? null,
        title: this.form.title.trim(),
        channels: this.form.channels.map((channel) => ({
          label: channel.label.trim(),
          type: channel.type,
          value: channel.value.trim(),
          iconUrl: channel.iconUrl || null,
          iconFile: channel.iconFile || null
        }))
      };
    },
    errorMessage(res) {
      if (SAVE_ERRORS[res.code]) return SAVE_ERRORS[res.code];
      if (res.httpCode === 404) return 'El perfil ya no existe. Cierra y recarga la lista.';
      if (res.httpCode === 400 && res.message) return `Revisa los datos: ${res.message}`;
      return 'Ocurrió un error al guardar el perfil, intenta más tarde.';
    },
    validate() {
      if (this.$refs.profileForm.validate()) this.save();
    },
    async save() {
      this.loading = true;
      this.formError = '';
      const payload = this.buildPayload();
      const res =
        this.profile === null
          ? await RefundRedirectProfilesService.create(payload)
          : await RefundRedirectProfilesService.update(this.profile.id, payload);
      this.loading = false;
      if (res.status !== 'ok') {
        this.formError = this.errorMessage(res);
        return;
      }
      this.$emit('saved', res.data);
      this.closeDialog();
    },
    closeDialog() {
      this.$emit('close-dialog');
    }
  }
};
</script>
